// frontend/src/lib/apiClient.js

// --- helpers ---------------------------------------------------------------
function pickFromCsv(val) {
  if (!val) return val;
  const parts = String(val).split(",").map(s => s.trim()).filter(Boolean);
  if (!parts.length) return "";
  if (parts.length === 1) return parts[0];
  const isLocal = (typeof window !== "undefined") && /localhost|127\.0\.0\.1/.test(window.location.hostname);
  if (isLocal) {
    return parts.find(p => /localhost|127\.0\.0\.1/.test(p)) || parts[0];
  } else {
    return parts.find(p => !/localhost|127\.0\.0\.1/.test(p)) || parts[0];
  }
}

function resolveBase() {
  let raw = "";
  try {
    if (typeof import.meta !== "undefined" && import.meta.env) {
      raw = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || raw;
    }
  } catch {}
  try {
    if (!raw && typeof process !== "undefined" && process.env) {
      raw = process.env.REACT_APP_API_URL || process.env.API_BASE_URL || process.env.API_URL || raw;
    }
  } catch {}
  try {
    if (!raw && typeof window !== "undefined" && window.__API_BASE_URL__) {
      raw = window.__API_BASE_URL__;
    }
  } catch {}

  let selected = pickFromCsv(raw);
  if (!selected && typeof window !== "undefined" && /localhost|127\.0\.0\.1/.test(window.location.hostname)) {
    selected = "http://localhost:7071";
  }
  if (selected && selected.endsWith("/")) selected = selected.slice(0, -1);

  console.debug("[apiClient] API base raw =", raw || "(empty)");
  console.debug("[apiClient] API base selected =", selected || "(empty)");
  return selected || "";
}

function getAuthHeaders() {
  try {
    const token = (typeof localStorage !== "undefined" && localStorage.getItem("token")) || "";
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

function isJson(resp) {
  const ct = resp.headers?.get?.("content-type") || "";
  return ct.includes("application/json");
}

async function readJsonOrPreview(resp) {
  const text = await resp.text();
  if (!resp.ok) {
    const preview = text.length > 200 ? text.slice(0, 200) + "..." : text;
    throw new Error(`HTTP ${resp.status} ${resp.statusText} – ${preview}`);
  }
  if (!text) return null;
  if (!isJson(resp)) {
    const preview = text.slice(0, 200);
    throw new Error(`Expected JSON but got '${resp.headers.get("content-type")}'. Body preview: ${preview}`);
  }
  return JSON.parse(text);
}

function withTimeout(ms) {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(new Error(`Request timed out after ${ms}ms`)), ms);
  return { signal: ctrl.signal, cancel: () => clearTimeout(id) };
}

async function doFetch(url, init, { timeoutMs = 15000, retries = 1 } = {}) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    const { signal, cancel } = withTimeout(timeoutMs);
    try {
      const resp = await fetch(url, { ...init, signal });
      cancel();
      return await readJsonOrPreview(resp);
    } catch (err) {
      cancel();
      lastErr = err;
      // retry only on network/Abort errors or 5xx we surface via error message
      const msg = String(err?.message || "");
      const transient = /timed out|Failed to fetch|NetworkError|HTTP 5\d{2}/i.test(msg);
      if (/timed out/i.test(msg)) {
        try {
          console.warn("[apiClient] request timed out", {
            url,
            timeoutMs,
            attempt,
            retries,
            method: init?.method || "GET",
          });
        } catch {}
      }
      if (!transient || attempt === retries) throw err;
      await new Promise(r => setTimeout(r, 300 * (attempt + 1)));
    }
  }
  throw lastErr;
}

// --- public client ---------------------------------------------------------
const BASE = resolveBase();

export const apiClient = {
  base: BASE,

  get(path, { query, headers, timeoutMs, retries, credentials = "include" } = {}) {
    const qs = query
      ? "?" + Object.entries(query)
          .filter(([, v]) => v !== undefined && v !== null && v !== "")
          .map(([k, v]) =>
            Array.isArray(v)
              ? v.map((vv) => `${encodeURIComponent(k)}=${encodeURIComponent(vv)}`).join("&")
              : `${encodeURIComponent(k)}=${encodeURIComponent(v)}`
          )
          .join("&")
      : "";
    const url = BASE + path + qs;
    console.debug("[apiClient] GET", url, query || {});
    return doFetch(
      url,
      {
        method: "GET",
        credentials,
        headers: { ...getAuthHeaders(), ...(headers || {}) },
      },
      { timeoutMs, retries }
    );
  },

  post(path, body, { headers, timeoutMs, retries, credentials = "include" } = {}) {
    const url = BASE + path;
    const isFD = (typeof FormData !== 'undefined') && (body instanceof FormData);
    console.debug("[apiClient] POST", url, isFD ? "[FormData]" : body);
    const baseHeaders = { ...getAuthHeaders(), ...(headers || {}) };
    const init = {
      method: "POST",
      credentials,
      headers: isFD ? baseHeaders : { "Content-Type": "application/json", ...baseHeaders },
      body: isFD ? body : JSON.stringify(body || {}),
    };
    return doFetch(url, init, { timeoutMs, retries });
  },

  put(path, body, { headers, timeoutMs, retries, credentials = "include" } = {}) {
    const url = BASE + path;
    const isFD = (typeof FormData !== 'undefined') && (body instanceof FormData);
    console.debug("[apiClient] PUT", url, isFD ? "[FormData]" : body);
    const baseHeaders = { ...getAuthHeaders(), ...(headers || {}) };
    const init = {
      method: "PUT",
      credentials,
      headers: isFD ? baseHeaders : { "Content-Type": "application/json", ...baseHeaders },
      body: isFD ? body : JSON.stringify(body || {}),
    };
    return doFetch(url, init, { timeoutMs, retries });
  },
};
